/**
 * cart-gift-picker.js
 * Owns the shared gift-picker bottom sheet (snippets/cart-gift-picker-modal.liquid):
 * open/close, focus trap, keeping its content in sync with the live cart,
 * and auto-opening on every add / quantity increase (not just once per
 * promo per session — see the long comment above maybeAutoOpen() below).
 * Ported from grid-2027's assets/cart-gift-picker.js (same behavior
 * contract), adapted for Horizon's own event name (`cart:update`, not
 * `cart:updated`) and drawer API (`cart-drawer-component`'s `.open()`
 * method, not `window.cartDrawer`).
 *
 * Does NOT contain any promo logic itself — the coffret/litre selection,
 * stepper, and reconciler behavior all still live entirely in
 * cart-bogo-routines.js / cart-bogo-litres.js, unchanged. This script only
 * moves already-rendered markup into view and manages the sheet chrome.
 *
 * Content sync: [data-gift-sections] (rendered hidden inside
 * cart-items-component by cart-drawer.liquid, kept fresh by every section
 * morph) is copied via innerHTML string assignment into
 * [data-gift-modal-body] (in the static sheet, outside the morphed region)
 * on load and on every `cart:update`. A plain string copy, not a DOM move:
 * an already-connected custom element that gets reparented via appendChild
 * fires disconnectedCallback then connectedCallback again on the SAME
 * node, which would re-run <cart-bogo-routines-component>'s/
 * <cart-bogo-litres-component>'s connectedCallback listener-binding a
 * second time on the same buttons -- a real double-binding bug. Assigning
 * innerHTML instead parses a fresh set of nodes into the sheet each time,
 * each getting exactly one clean connectedCallback firing.
 */
(function () {
  'use strict';

  var lastFocused = null;

  /* Tracks whether the sheet is SUPPOSED to be open, independent of the
   * overlay element's own `is-open` class — see the long comment on the
   * `cart:update` listener below for why the class alone can't be trusted:
   * a later, independent cart mutation (e.g. a BOGO reconciler's own
   * debounced follow-up request) can morph this snippet's section again
   * and silently strip `is-open` off an overlay the customer never closed.
   * `reassertOpenState()` uses this flag to put the class back, without
   * re-stealing focus the way a full `openModal()` call would. */
  var isSheetOpen = false;

  function syncContent() {
    var source = document.querySelector('[data-gift-sections]');
    var modalBody = document.querySelector('[data-gift-modal-body]');
    if (!source || !modalBody) return;
    modalBody.innerHTML = source.innerHTML;
  }

  /**
   * Whether the sheet currently has at least one promo section to show
   * (routines and/or litres each render their own [data-gift-section]
   * only while has_trigger_item is true). Checked after every
   * syncContent() call that could change this, so the sheet is never
   * left open showing nothing.
   * @returns {boolean}
   */
  function hasAnyGiftSection() {
    var modalBody = document.querySelector('[data-gift-modal-body]');
    return !!(modalBody && modalBody.querySelector('[data-gift-section]'));
  }

  function getFocusable(panel) {
    return panel.querySelectorAll(
      'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    );
  }

  function handleKeydown(e) {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay || !overlay.classList.contains('is-open')) return;

    if (e.key === 'Escape') {
      closeModal();
      return;
    }

    if (e.key !== 'Tab') return;
    var panel = document.querySelector('[data-gift-modal-panel]');
    if (!panel) return;
    var focusable = getFocusable(panel);
    if (!focusable.length) return;
    var first = focusable[0];
    var last = focusable[focusable.length - 1];
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal() {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay) return;
    syncContent();
    /* Never open onto a blank sheet — a real, confirmed report ("un pop
     * bizarre" with nothing inside it) came from exactly this: a trigger
     * removed just before the sheet opened (or a reconciler pass still
     * in flight) left has_trigger_item false for both promos, so neither
     * [data-gift-section] rendered, and the sheet showed only its own
     * static "Vos cadeaux gratuits" header with nothing below it. */
    if (!hasAnyGiftSection()) return;
    isSheetOpen = true;
    lastFocused = document.activeElement;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
    var closeBtn = overlay.querySelector('[data-gift-modal-close]');
    if (closeBtn) closeBtn.focus();
  }

  function closeModal() {
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    isSheetOpen = false;
    if (!overlay) return;
    overlay.classList.remove('is-open');
    overlay.setAttribute('aria-hidden', 'true');
    if (lastFocused && typeof lastFocused.focus === 'function') {
      lastFocused.focus();
    }
    lastFocused = null;
  }

  /**
   * Re-applies the open class/aria-hidden state to whatever overlay
   * element currently exists in the DOM, WITHOUT touching focus or
   * lastFocused — unlike openModal(), this must be safe to call on every
   * cart:update while the sheet is legitimately already open (e.g. because
   * the customer's own stepper click inside the sheet is itself what
   * triggered this cart:update). Only fixes up an overlay that a section
   * morph silently reset to closed; a no-op when isSheetOpen is false or
   * the class is already correct.
   */
  function reassertOpenState() {
    if (!isSheetOpen) return;
    var overlay = document.querySelector('[data-gift-modal-overlay]');
    if (!overlay || overlay.classList.contains('is-open')) return;
    overlay.classList.add('is-open');
    overlay.setAttribute('aria-hidden', 'false');
  }

  /**
   * Opens the cart drawer itself, if it isn't already open. Horizon's
   * <cart-drawer-component> extends DialogComponent and exposes its own
   * `open()` method (handles the Meta in-app-browser redirect edge case
   * too) — calling it when the drawer's <dialog> is already open is a
   * safe no-op (DialogComponent.showDialog() early-returns if `dialog.open`
   * is already true).
   */
  function openCartDrawer() {
    var drawer = document.querySelector('cart-drawer-component');
    if (drawer && typeof drawer.open === 'function') {
      drawer.open();
    }
  }

  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-gift-picker-open]')) {
      openModal();
      return;
    }
    if (e.target.closest('[data-gift-modal-close]')) {
      closeModal();
      return;
    }
    if (e.target.matches('[data-gift-modal-overlay]')) {
      closeModal();
    }
  });

  document.addEventListener('keydown', handleKeydown);

  /* ── Auto-open on every add / quantity increase of a TRIGGER-eligible ────
   * product ─────────────────────────────────────────────────────────────
   * A gift is auto-picked by default (the reconciler's default-fill, see
   * cart-bogo-routines.js / cart-bogo-litres.js) with no click required, so
   * a customer can easily miss that it's actually changeable. Explicit
   * merchant request: pop the gift sheet open every time a product is
   * added to cart or a line's quantity is increased (not just the first
   * time per session) whenever a routines/litres section is currently
   * showing, so the choice is never missed no matter how many times the
   * customer touches their cart.
   *
   * Tracks the cart's TRIGGER-ELIGIBLE quantity — the sum of quantities
   * across lines whose variant is in a currently-enabled promo's
   * triggerVariantIds (read from the [data-bogo-routines-config]/
   * [data-bogo-litres-config] JSON blocks those snippets render
   * unconditionally whenever their own promo is enabled, independent of
   * has_trigger_item — see .claude/rules/cart-promotions.md) — not the
   * cart's total item_count. A real, confirmed report: adding an unrelated
   * product that has nothing to do with either promo (e.g. a plain
   * shampoo, while a coffret/litre promo is already active from an
   * earlier purchase) still increased cart.item_count and popped the
   * sheet open, even though nothing about the customer's actual
   * entitlement changed. Comparing trigger-eligible quantity instead of
   * raw item_count fixes this: only a genuine coffret/litre purchase (or
   * quantity bump) can increase it.
   *
   * Also excludes gift-flagged lines (_free_gift/_tier_gift/
   * _bogo_routines_gift/_bogo_litres_gift) from the sum — trigger and
   * display collections are commonly configured to be the SAME collection
   * (see cart-promotions.md), so a picked gift's own variant can itself be
   * trigger-eligible; without this exclusion, picking a gift would
   * silently inflate the very quantity meant to represent "new genuine
   * purchases", matching the theme-wide "gift lines never earn more gifts"
   * rule.
   *
   * Deliberately still scoped to `cart:update` only, never a plain
   * page-load sync: a passive page navigation that happens to land on a
   * page with an already-qualifying cart must not itself pop open the
   * cart drawer + sheet unprompted. `cart:update` firing already implies
   * the customer just took an action (add/remove/change).
   */
  var lastKnownTriggerQty = null; // null until seeded once from the real cart

  /**
   * Reads the trigger variant id set for whichever BOGO promos are
   * currently enabled. Both config blocks live inside [data-gift-sections]
   * (hidden, not removed) so they're always present in the DOM whenever
   * their promo is on, and stay fresh across every section morph.
   * @returns {Set<number>}
   */
  function getTriggerVariantIds() {
    var ids = new Set();
    ['[data-bogo-routines-config]', '[data-bogo-litres-config]'].forEach(function (selector) {
      var el = document.querySelector(selector);
      if (!el) return;
      try {
        var config = JSON.parse(el.textContent);
        (config.triggerVariantIds || []).forEach(function (id) {
          ids.add(Number(id));
        });
      } catch (e) {
        /* Malformed/missing config — this promo just contributes no ids. */
      }
    });
    return ids;
  }

  /**
   * @param {object} item - a /cart.js line item
   * @returns {boolean}
   */
  function isGiftLine(item) {
    var props = item.properties || {};
    return (
      props['_free_gift'] === 'true' ||
      props['_tier_gift'] === 'true' ||
      props['_bogo_routines_gift'] === 'true' ||
      props['_bogo_litres_gift'] === 'true'
    );
  }

  /**
   * Refreshes lastKnownTriggerQty from the live cart and reports whether
   * trigger-eligible quantity is now higher than what was last recorded.
   * A no-op "false" until the first successful fetch has run (covers both
   * the initial page-load seed and any transient fetch failure) — there is
   * deliberately no separate seed step: the very first call this session
   * establishes the baseline without ever reporting an increase, since
   * `lastKnownTriggerQty === null` short-circuits before any comparison.
   * @returns {Promise<boolean>}
   */
  function refreshAndCheckIncrease() {
    return fetch('/cart.js')
      .then(function (res) {
        return res.ok ? res.json() : null;
      })
      .then(function (cart) {
        if (!cart) return false;
        var triggerIds = getTriggerVariantIds();
        var triggerQty = 0;
        (cart.items || []).forEach(function (item) {
          if (isGiftLine(item)) return;
          if (triggerIds.has(Number(item.variant_id))) {
            triggerQty += item.quantity;
          }
        });
        var wasSeeded = lastKnownTriggerQty !== null;
        var increased = wasSeeded && triggerQty > lastKnownTriggerQty;
        lastKnownTriggerQty = triggerQty;
        return increased;
      })
      .catch(function () {
        /* Network hiccup — treat as "no increase" for this pass; the next
           cart:update retries with a fresh fetch. Leaves lastKnownTriggerQty
           untouched (still whatever it was, possibly still null), so a
           still-unseeded baseline keeps skipping rather than risk a false
           positive against no real data. */
        return false;
      });
  }

  function maybeAutoOpen() {
    var sections = document.querySelectorAll('[data-gift-sections] [data-gift-section]');
    if (!sections.length) return;
    openCartDrawer();
    openModal();
  }

  /* Serializes every refreshAndCheckIncrease() call strictly one after
   * another rather than letting concurrent cart:update events (this
   * theme's own action plus a same-tick reconciler reaction) fire off
   * overlapping /cart.js fetches whose responses could resolve out of
   * order and clobber lastKnownTriggerQty with a stale value. A single
   * aggregate counter makes this far less likely to cause a visible bug
   * than per-line tracking did, but chaining every check through one
   * promise costs nothing and removes the ordering question entirely —
   * each check's fetch only starts once the previous one has fully
   * resolved and written its result. */
  var checkQueue = Promise.resolve();

  /* Keep the sheet's copy fresh even while it's open (or about to reopen)
     so a stepper click made just before another cart change doesn't leave
     it showing stale quantities. `cart:update` is Horizon's real event name
     (ThemeEvents.cartUpdate in @theme/events) — confirmed against
     assets/events.js, not the grid-2027 theme's `cart:updated`.
   *
   * MUST be deferred with setTimeout, not run synchronously in this
   * handler. Confirmed live against Horizon's own source
   * (assets/component-cart-items.js): `this.dispatchEvent(new
   * CartUpdateEvent(...))` — which is what fires `cart:update` — is called
   * BEFORE the following `morphSection(this.sectionId, ...)` line, and
   * morphSection's own body (assets/section-renderer.js) has no internal
   * `await`, so it runs fully synchronously despite being declared
   * `async`. That means a synchronous `cart:update` handler runs and sets
   * `.is-open` on the overlay *before* the section morph happens — and the
   * morph then diffs the live (now-open) DOM against the freshly rendered
   * (always-closed) server markup for this snippet, stripping the
   * `is-open` class right back off milliseconds later. This is the
   * opposite of grid-2027's own `cart:updated`, which that theme dispatches
   * only *after* its DOM replacement completes (see cart-gift-picker.js's
   * own doc comment there) — the naive one-to-one port of that listener
   * silently broke auto-open on Horizon specifically. A `setTimeout(fn, 0)`
   * defers this handler's work to the next macrotask, after the triggering
   * function's synchronous work (dispatch + morph) has fully finished,
   * regardless of which cart-mutating component fired the event.
   *
   * The defer alone still isn't the full fix: it only protects against the
   * morph tied to THIS SAME event. A BOGO reconciler (cart-bogo-routines.js
   * / cart-bogo-litres.js) also listens for cart:update and may, after its
   * own ~150ms debounce and a further network round trip, issue a SEPARATE
   * /cart/change.js or /cart/add.js call — its own independent morph+
   * dispatch cycle, arriving well after this handler's setTimeout already
   * ran. That later, independent morph strips `is-open` again with nothing
   * to put it back (e.g. a reconciler trim that itself dispatches its own
   * cart:update, with no item-count increase of its own to trigger a fresh
   * auto-open). reassertOpenState() closes that gap: it runs on every
   * deferred cart:update regardless of the increase check below, and
   * re-applies `is-open` whenever isSheetOpen says the sheet should still
   * be open but a morph silently reset the class — eventually consistent
   * across however many independent update waves a single cart action
   * triggers. */
  document.addEventListener('cart:update', function () {
    setTimeout(function () {
      syncContent();
      /* If the sheet is open and the cart change just emptied it out
       * (e.g. the trigger product was removed and has_trigger_item
       * flipped false for both promos), close it rather than reasserting
       * an open state onto a now-blank sheet — see openModal()'s own
       * comment on the same "un pop bizarre" report. */
      if (isSheetOpen && !hasAnyGiftSection()) {
        closeModal();
        return;
      }
      reassertOpenState();
      checkQueue = checkQueue
        .then(function () {
          return refreshAndCheckIncrease();
        })
        .then(function (increased) {
          if (increased) maybeAutoOpen();
        });
    }, 0);
  });

  /* Initial sync so the CTA + sheet content matches page-load cart state
     even before any cart:update fires (the drawer's own markup is
     server-rendered on every page load, not only via AJAX), plus seeding
     lastKnownTriggerQty for the increase check above via the same
     refreshAndCheckIncrease() used everywhere else — its own null-baseline
     guard means this first call can never itself report an increase, so
     it's safe to fire-and-forget without checking its result. Deliberately
     does NOT call maybeAutoOpen(): a passive page load landing on an
     already-qualifying cart must not itself pop the drawer + sheet open
     unprompted, only a genuine cart:update action should. */
  syncContent();
  checkQueue = checkQueue.then(function () {
    return refreshAndCheckIncrease();
  });
})();
